'use client'

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { cva, type VariantProps } from 'class-variance-authority'
import { Circle } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const radioGroupItemVariants = cva(
  'aspect-square rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        default: 'h-4 w-4',
        // Tablet line control (cafeteria serve, PROD-1278): a 32px ring with a
        // 14px dot — the milk pick a worker taps between trays.
        lg: 'size-8',
      },
    },
    defaultVariants: { size: 'default' },
  },
)

const radioGroupIndicatorVariants = cva('fill-current text-current', {
  variants: {
    size: {
      default: 'h-2.5 w-2.5',
      lg: 'size-3.5',
    },
  },
  defaultVariants: { size: 'default' },
})

export interface RadioGroupItemProps
  extends
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioGroupItemProps>(
  ({ className, size, ...props }, ref) => {
    return (
      <RadioGroupPrimitive.Item ref={ref} className={cn(radioGroupItemVariants({ size }), className)} {...props}>
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className={radioGroupIndicatorVariants({ size })} />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
    )
  },
)
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem, radioGroupItemVariants }
